import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Stage, Layer, Image as KonvaImage, Text as KonvaText, Rect, Transformer, Group } from 'react-konva';
import useImage from 'use-image';

// Reusable image component for Konva
export function URLImage({ src, ...props }) {
  const [image] = useImage(src, 'anonymous');
  return <KonvaImage image={image} {...props} />;
}

// Main canvas workspace
export function CanvasWorkspace({
  width = 1080,
  height = 1080,
  elements = [],
  selectedId,
  onSelect,
  onElementChange,
  backgroundImage,
  backgroundColor = '#ffffff',
  filters = {},
  stageRef,
  scale = 1,
}) {
  const transformerRef = useRef(null);
  const [bgImage] = useImage(backgroundImage, 'anonymous');

  useEffect(() => {
    if (transformerRef.current) {
      const stage = transformerRef.current.getStage();
      if (selectedId) {
        const node = stage.findOne(`#${selectedId}`);
        if (node) {
          transformerRef.current.nodes([node]);
          transformerRef.current.getLayer().batchDraw();
        }
      } else {
        transformerRef.current.nodes([]);
        transformerRef.current.getLayer().batchDraw();
      }
    }
  }, [selectedId]);

  const handleStageClick = (e) => {
    if (e.target === e.target.getStage() || e.target.attrs.id === 'background') {
      onSelect(null);
    }
  };

  const handleDragEnd = (id, e) => {
    onElementChange(id, { x: e.target.x(), y: e.target.y() });
  };

  const handleTransformEnd = (id, e) => {
    const node = e.target;
    onElementChange(id, {
      x: node.x(),
      y: node.y(),
      width: Math.max(5, node.width() * node.scaleX()),
      height: Math.max(5, node.height() * node.scaleY()),
      rotation: node.rotation(),
      scaleX: 1,
      scaleY: 1,
    });
    node.scaleX(1);
    node.scaleY(1);
  };

  // CSS filter string for the canvas
  const filterStyle = {};
  if (filters.brightness !== undefined || filters.contrast !== undefined || 
      filters.saturate !== undefined || filters.grayscale !== undefined ||
      filters.sepia !== undefined || filters.blur !== undefined) {
    const parts = [];
    if (filters.brightness !== undefined) parts.push(`brightness(${filters.brightness}%)`);
    if (filters.contrast !== undefined) parts.push(`contrast(${filters.contrast}%)`);
    if (filters.saturate !== undefined) parts.push(`saturate(${filters.saturate}%)`);
    if (filters.grayscale !== undefined) parts.push(`grayscale(${filters.grayscale}%)`);
    if (filters.sepia !== undefined) parts.push(`sepia(${filters.sepia}%)`);
    if (filters.blur !== undefined) parts.push(`blur(${filters.blur}px)`);
    filterStyle.filter = parts.join(' ');
  }

  const displayWidth = width * scale;
  const displayHeight = height * scale;

  return (
    <div 
      className="canvas-workspace relative inline-block" 
      style={{ width: displayWidth, height: displayHeight, ...filterStyle }}
      data-testid="canvas-workspace"
    >
      <Stage
        ref={stageRef}
        width={displayWidth}
        height={displayHeight}
        scaleX={scale}
        scaleY={scale}
        onClick={handleStageClick}
        onTap={handleStageClick}
      >
        <Layer>
          {/* Background */}
          <Rect
            id="background"
            x={0} y={0}
            width={width} height={height}
            fill={backgroundColor}
          />
          {bgImage && (
            <KonvaImage
              id="background"
              image={bgImage}
              x={0} y={0}
              width={width} height={height}
            />
          )}

          {/* Elements */}
          {elements.map((el) => {
            if (el.type === 'text') {
              return (
                <KonvaText
                  key={el.id}
                  id={el.id}
                  x={el.x || 0}
                  y={el.y || 0}
                  text={el.text || 'Text'}
                  fontSize={el.fontSize || 48}
                  fontFamily={el.fontFamily || "'Space Grotesk', sans-serif"}
                  fill={el.fill || '#ffffff'}
                  fontStyle={el.fontStyle || 'normal'}
                  align={el.align || 'center'}
                  width={el.width || 800}
                  draggable
                  onClick={() => onSelect(el.id)}
                  onTap={() => onSelect(el.id)}
                  onDragEnd={(e) => handleDragEnd(el.id, e)}
                  onTransformEnd={(e) => handleTransformEnd(el.id, e)}
                  rotation={el.rotation || 0}
                />
              );
            }
            if (el.type === 'image') {
              return (
                <URLImage
                  key={el.id}
                  id={el.id}
                  src={el.src}
                  x={el.x || 0}
                  y={el.y || 0}
                  width={el.width || 400}
                  height={el.height || 400}
                  draggable
                  onClick={() => onSelect(el.id)}
                  onTap={() => onSelect(el.id)}
                  onDragEnd={(e) => handleDragEnd(el.id, e)}
                  onTransformEnd={(e) => handleTransformEnd(el.id, e)}
                  rotation={el.rotation || 0}
                />
              );
            }
            if (el.type === 'rect') {
              return (
                <Rect
                  key={el.id}
                  id={el.id}
                  x={el.x || 0}
                  y={el.y || 0}
                  width={el.width || 200}
                  height={el.height || 200}
                  fill={el.fill || '#1e3a5f'}
                  opacity={el.opacity !== undefined ? el.opacity : 1}
                  cornerRadius={el.cornerRadius || 0}
                  draggable
                  onClick={() => onSelect(el.id)}
                  onTap={() => onSelect(el.id)}
                  onDragEnd={(e) => handleDragEnd(el.id, e)}
                  onTransformEnd={(e) => handleTransformEnd(el.id, e)}
                  rotation={el.rotation || 0}
                />
              );
            }
            return null;
          })}

          {/* Transformer */}
          <Transformer
            ref={transformerRef}
            boundBoxFunc={(oldBox, newBox) => {
              if (newBox.width < 5 || newBox.height < 5) return oldBox;
              return newBox;
            }}
          />
        </Layer>
      </Stage>
    </div>
  );
}

export default CanvasWorkspace;
